/**
 * `/signin` — the only unauthenticated screen.
 *
 * Google is the sole identity provider: the popup returns a Firebase ID token, the
 * server verifies it (signature, issuer, audience, `@botpresso.com` domain) and sets
 * the httpOnly session cookie. Nothing about role or workspace is chosen here or sent
 * from here; `/me` decides that after the cookie exists.
 *
 * With no Firebase environment variables (dev, CI) the button is replaced by a plain
 * explanation rather than a crash.
 *
 * When the server reports `devAutoLogin` — which it does only when `APP_ENV` is
 * exactly `development` — this screen signs straight in as the developer account
 * instead of asking. The decision is the server's, not the bundle's: a production
 * build served by a production server can never take this path, whatever the
 * bundle was compiled with.
 */
import { LogIn, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Btn, Card, Spinner } from "@/components/ui";
import { api, isApiError } from "@/lib/api";
import {
  firebaseConfigured,
  isSignInCancelled,
  missingFirebaseKeys,
  signInWithGoogle,
} from "@/lib/firebase";
import { useSession } from "@/lib/session";
import { LANDING_ROUTE } from "./Sidebar";

interface AuthMode {
  firebaseConfigured: boolean;
  devAutoLogin: boolean;
  devLoginEmail: string | null;
}

export function SignIn() {
  const { me, ready, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<AuthMode | null>(null);
  // React 18 StrictMode runs effects twice in development — exactly where this
  // path is active — so without a latch the auto-login fires two sessions.
  const autoLoginStarted = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void api
      .get<AuthMode>("/auth/mode")
      .then((value) => {
        if (!cancelled) setMode(value);
      })
      .catch(() => {
        if (!cancelled) setMode({ firebaseConfigured, devAutoLogin: false, devLoginEmail: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ready || me || !mode?.devAutoLogin || autoLoginStarted.current) return;
    autoLoginStarted.current = true;

    setBusy(true);
    void (async () => {
      try {
        await api.post("/auth/dev-session", {});
        await refresh();
        navigate((location.state as { from?: string } | null)?.from ?? "/", { replace: true });
      } catch (cause) {
        setError(
          isApiError(cause)
            ? cause.message
            : "Developer sign-in failed. Is the server running with APP_ENV=development?",
        );
      } finally {
        setBusy(false);
      }
    })();
  }, [ready, me, mode, refresh, navigate, location.state]);

  if (!ready) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        <Spinner size={22} label="Loading" />
      </div>
    );
  }

  if (me) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? LANDING_ROUTE[me.workspace]} replace />;
  }

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      const { idToken } = await signInWithGoogle();
      await api.post("/auth/session", { idToken });
      await refresh();
      navigate((location.state as { from?: string } | null)?.from ?? "/", { replace: true });
    } catch (cause) {
      if (isSignInCancelled(cause)) {
        setError(null);
      } else if (isApiError(cause)) {
        setError(cause.message);
      } else {
        setError("Sign-in failed. Try again, or contact HR if it keeps happening.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    // Frame padding and card width live in `styles/shell.css` (`.bp-signin`) so a phone
    // can reclaim them; the 94vw card cap plus 24 px of padding overran a 390 px screen.
    <div
      className="bp-signin"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--bg)",
      }}
    >
      <Card className="bp-signin__card">
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 22 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              background: "var(--violet)",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-head)",
              fontWeight: 800,
              color: "#fff",
              fontSize: 15,
            }}
            aria-hidden="true"
          >
            B
          </div>
          <div>
            <div
              style={{
                fontFamily: "var(--font-head)",
                fontWeight: 800,
                fontSize: 16,
                color: "var(--ink)",
                letterSpacing: -0.3,
              }}
            >
              Botpresso<span style={{ color: "var(--violet)" }}>HR</span>
            </div>
            <div
              style={{
                fontFamily: "var(--font-body)",
                fontSize: 10,
                color: "var(--muted)",
                fontWeight: 600,
              }}
            >
              People operations
            </div>
          </div>
        </div>

        <h1
          style={{
            fontFamily: "var(--font-head)",
            fontWeight: 800,
            fontSize: 22,
            letterSpacing: -0.5,
            color: "var(--ink)",
            margin: "0 0 6px",
          }}
        >
          Sign in
        </h1>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: 13,
            color: "var(--muted)",
            margin: "0 0 20px",
            lineHeight: 1.6,
          }}
        >
          Use your <b style={{ color: "var(--ink)" }}>@botpresso.com</b> Google account. What you
          can see and do is set by HR — there is nothing to choose here.
        </p>

        {mode?.devAutoLogin ? (
          <div
            style={{
              background: "var(--violet-soft)",
              border: "1px solid var(--violet-line)",
              borderRadius: 10,
              padding: "12px 14px",
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--ink)",
              lineHeight: 1.6,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Spinner size={15} label="" />
            <span>
              Development mode — signing you in as{" "}
              <b style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>
                {mode.devLoginEmail}
              </b>
              .
            </span>
          </div>
        ) : firebaseConfigured ? (
          <Btn icon={LogIn} onClick={() => void signIn()} disabled={busy}>
            {busy ? "Signing in…" : "Continue with Google"}
          </Btn>
        ) : (
          <div
            style={{
              background: "var(--amber-soft)",
              border: "1px solid var(--amber-line)",
              borderRadius: 10,
              padding: "12px 14px",
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--ink)",
              lineHeight: 1.6,
            }}
          >
            Google sign-in isn&rsquo;t configured in this environment. Missing:{" "}
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>
              {missingFirebaseKeys.join(", ")}
            </span>
            .
          </div>
        )}

        {error ? (
          <div
            role="alert"
            style={{
              marginTop: 14,
              background: "var(--red-wash)",
              border: "1px solid var(--danger-line)",
              borderRadius: 10,
              padding: "10px 13px",
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--red)",
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            marginTop: 22,
            paddingTop: 16,
            borderTop: "1px solid var(--line)",
          }}
        >
          <ShieldCheck size={15} color="var(--violet)" style={{ marginTop: 1, flexShrink: 0 }} />
          <div
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 11,
              color: "var(--muted)",
              lineHeight: 1.6,
            }}
          >
            Sessions are server-side and expire automatically. Signing out ends the session
            everywhere.
          </div>
        </div>
      </Card>
    </div>
  );
}

export default SignIn;
